import * as React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Text } from '@antimatterhq/ui/components/typography';

type PaginationProps = React.ComponentProps<'nav'> & {
  className?: string;
};

type PaginationContentProps = React.ComponentProps<'ul'> & {
  className?: string;
};

type PaginationItemProps = React.ComponentProps<'li'> & {
  className?: string;
};

type PaginationLinkProps = React.ComponentProps<'a'> & {
  isActive?: boolean;
  disabled?: boolean;
  hasLabel?: boolean;
  className?: string;
};

const Pagination = ({ className, ...props }: PaginationProps) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  PaginationContentProps
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-1', className)}
    {...props}
  />
));

const PaginationItem = React.forwardRef<HTMLLIElement, PaginationItemProps>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn('cursor-pointer select-none list-none', className)}
      {...props}
    />
  ),
);

const PaginationLink = ({
  className,
  isActive,
  disabled,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    aria-disabled={disabled}
    className={cn(
      'focus-visible:ring-ring inline-flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md px-2.5 text-sm font-medium text-grey-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none dark:text-grey-200',
      isActive
        ? 'bg-blue-600/16 font-bold text-blue-700 dark:bg-blue-400/16 dark:text-blue-400'
        : 'hover:bg-blue-600/8 hover:text-blue-700 dark:text-grey-200 dark:hover:bg-blue-400/8 dark:hover:text-blue-400',
      disabled && 'pointer-events-none opacity-50',
      className,
    )}
    {...props}
  />
);

const PaginationPrevious = ({
  className,
  hasLabel,
  ...props
}: PaginationLinkProps) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn('gap-1 pl-2.5 text-grey-700 dark:text-grey-200', className)}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
    {hasLabel && <Text size="sm">Previous</Text>}
  </PaginationLink>
);

const PaginationNext = ({
  className,
  hasLabel,
  ...props
}: PaginationLinkProps) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn('gap-1 pr-2.5 text-grey-700 dark:text-grey-200', className)}
    {...props}
  >
    {hasLabel && <Text size="sm">Next</Text>}
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
);

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'> & {
  className?: string;
}) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4 text-grey-500 dark:text-grey-350" />
    <span className="sr-only">More pages</span>
  </span>
);

Pagination.displayName = 'Pagination';
PaginationContent.displayName = 'PaginationContent';
PaginationItem.displayName = 'PaginationItem';
PaginationLink.displayName = 'PaginationLink';
PaginationPrevious.displayName = 'PaginationPrevious';
PaginationNext.displayName = 'PaginationNext';
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
